import moment from 'moment';
import { useFormContext } from 'react-hook-form';
import { WebLink2 } from 'ui/theme';
import { FormField } from 'ui/formField';
import { CHECKBOX_STATUS } from 'ui/checkbox';
import { DatePickerField, InputField, RadioListField } from 'lib/iterable-hook-form/FormField';
import { DataPolicy } from '../types';
import {
  ORGANIZATION_NAME,
  WEBSITE,
  SALESFORCE_ACCOUNT_ID,
  TRIAL_END_DATE,
  DATA_POLICY,
} from '../constants';
import { FormSection } from './FormSection';
import { HipaaDescription } from './HipaaDescription';
import {
  CheckboxGrid,
  CheckboxWithMarginTop,
  TrialPeriodDescription,
  TrialEndDateFormField,
  LightBody2,
} from './FormSection.styled';
import type { NewOrganizationFormSchemaType } from '../contexts/NewOrganizationFormProvider';

export const TEXT = {
  HEADING: 'Organization',
  DESCRIPTION: 'Start by adding your customer’s organization details.',
  ORG_NAME_LABEL: 'Organization name',
  WEBSITE_LABEL: 'Website',
  SALESFORCE_ACCOUNT_ID_LABEL: 'Salesforce account ID',
  TRIAL_PERIOD_CHECKBOX_NAME: 'hasTrialEndDate',
  TRIAL_END_LABEL: 'Trial end date',
  DATA_POLICY_LABEL: 'Data policy',
  DATA_POLICY_STANDARD_NAME: 'Standard',
  DATA_POLICY_HIPAA_NAME: 'HIPAA',
};

type Props = {
  hasTrialEndDate: boolean;
  toggleHasTrialEndDate: () => void;
};

export const OrganizationFormSection = ({ hasTrialEndDate, toggleHasTrialEndDate }: Props) => {
  const {
    formState: { errors },
  } = useFormContext<NewOrganizationFormSchemaType>();

  const organizationNameError = errors.organizationName?.message;
  const websiteError = errors.website?.message;
  const salesforceAccountIdError = errors.salesforceAccountId?.message;

  return (
    <FormSection
      id="organization"
      headingText={TEXT.HEADING}
      descriptionText={TEXT.DESCRIPTION}
      scrollAnchorOffset={-240}
    >
      <FormField
        labelText={TEXT.ORG_NAME_LABEL}
        errorText={organizationNameError}
        hasError={!!organizationNameError}
        inputId={ORGANIZATION_NAME}
        isHorizontalLayout
      >
        <InputField id={ORGANIZATION_NAME} name={ORGANIZATION_NAME} />
      </FormField>

      <FormField
        labelText={TEXT.WEBSITE_LABEL}
        errorText={websiteError}
        hasError={!!websiteError}
        inputId={WEBSITE}
        isHorizontalLayout
      >
        <InputField id={WEBSITE} name={WEBSITE} placeholder="Example: https://www.example.com" />
      </FormField>

      <FormField
        labelText={TEXT.SALESFORCE_ACCOUNT_ID_LABEL}
        errorText={salesforceAccountIdError}
        hasError={!!salesforceAccountIdError}
        inputId={SALESFORCE_ACCOUNT_ID}
        isHorizontalLayout
      >
        <InputField id={SALESFORCE_ACCOUNT_ID} name={SALESFORCE_ACCOUNT_ID} />
      </FormField>

      <FormField labelText="Trial period" isHorizontalLayout>
        <CheckboxGrid>
          <CheckboxWithMarginTop
            name={TEXT.TRIAL_PERIOD_CHECKBOX_NAME}
            onClick={toggleHasTrialEndDate}
            status={hasTrialEndDate ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED}
          />
          <TrialPeriodDescription onClick={toggleHasTrialEndDate}>
            After the trial period expires members of this organization will no longer be able to
            log in to Iterable. You can always extend the trial or remove the trial to provide
            permanent access to the project once a customer signs their contract.
          </TrialPeriodDescription>

          {hasTrialEndDate && (
            <>
              <TrialEndDateFormField labelText={TEXT.TRIAL_END_LABEL} inputId={TRIAL_END_DATE}>
                <DatePickerField name={TRIAL_END_DATE} validDateRange={{ start: moment() }} />
              </TrialEndDateFormField>
            </>
          )}
        </CheckboxGrid>
      </FormField>

      <FormField labelText={TEXT.DATA_POLICY_LABEL} inputId={DATA_POLICY} isHorizontalLayout>
        <LightBody2>
          Set up your customer’s organization to appropriately store and process data.&nbsp;
          <WebLink2
          // TODO: Add Data Policy Docs link
          >
            Learn more in our knowledge center.
          </WebLink2>
        </LightBody2>
        <RadioListField
          name={DATA_POLICY}
          options={[
            {
              id: DataPolicy.UNRESTRICTED,
              name: TEXT.DATA_POLICY_STANDARD_NAME,
              value: DataPolicy.UNRESTRICTED,
              description: (
                <LightBody2>
                  The default for most customers who do not process ePHI (electronic protected
                  health information).
                </LightBody2>
              ),
            },
            {
              id: DataPolicy.HIPAA,
              name: TEXT.DATA_POLICY_HIPAA_NAME,
              value: DataPolicy.HIPAA,
              description: <HipaaDescription />,
            },
          ]}
        />
      </FormField>
    </FormSection>
  );
};
